import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { ProductInstanceStatus } from '@mfe/shared/schema-types';

import { Alert, parseAccountAlerts, setAlerts } from '../alerts';
import { selectUser } from '../auth';
import {
  getContractInstance,
  setContractInstance,
  setEcaError,
  setEcaIsLoading,
  setEcaModalType,
} from '../electronicCustomerAgreement/ecaSlice';
import { FetchWithErrorsQuery, graphqlQueryWithErrors } from '../graphql';
import { GET_CONTRACT_INSTANCE } from './requests';
import { ContractOperationErrorMessage, ContractOperationType, ContractStateReason, EcaModalType } from './types';

export function* ecaSaga(): SagaIterator {
  const {
    user: { relnId, productInstanceStatus, isFiberPreconnected },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  const isEligibleForEca =
    productInstanceStatus === ProductInstanceStatus.Preinstall ||
    (productInstanceStatus === ProductInstanceStatus.Active && isFiberPreconnected);

  // Only call getContractInstance for pre-install status and for active fiber customers that are pre-connected, as those are the only customers eligible to sign the ECA
  // Pre-install includes: Accepting, Accepted, and Activating lifecycle states
  if (!isEligibleForEca) {
    return;
  }

  try {
    yield put(setEcaIsLoading(true));

    const response: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: GET_CONTRACT_INSTANCE,
      variables: { input: { customerRelnId: relnId } },
      fetchPolicy: 'no-cache',
    });

    const { data, errors, runtimeError } = response;

    if (runtimeError || errors) {
      yield put(setEcaIsLoading(false));
      yield put(
        setEcaError({
          operation: ContractOperationType.getContractInstance,
          message: runtimeError?.message || ContractOperationErrorMessage.getContractInstanceFailed,
        })
      );
      return;
    }

    if (!data?.getContractInstance) {
      yield put(setEcaIsLoading(false));
      yield put(
        setEcaError({
          operation: ContractOperationType.getContractInstance,
          message: ContractOperationErrorMessage.noContractInstanceData,
        })
      );
      return;
    }

    const { getContractInstance } = data;
    const contractInstance = {
      contractId: getContractInstance.contractId,
      signedStatus: getContractInstance.signedStatus,
      signerUrl: getContractInstance.signerUrl,
      dateCreated: getContractInstance.dateCreated,
      dateModified: getContractInstance.dateModified,
      productInstanceId: getContractInstance.productInstanceId ?? null,
      customerId: getContractInstance.customerId,
      contractState: {
        state: getContractInstance.contractState.state,
        reason: getContractInstance.contractState.reason,
      },
    };
    yield put(setContractInstance(contractInstance));

    if (contractInstance.contractState.reason === ContractStateReason.Signed) {
      yield put(setEcaModalType(EcaModalType.Success));
    }

    yield put(setEcaIsLoading(false));
    const alerts: Alert[] = yield call(parseAccountAlerts);
    if (!alerts) {
      return;
    }

    yield put(setAlerts(alerts));
  } catch (error) {
    yield put(setEcaIsLoading(false));

    yield put(
      setEcaError({
        operation: ContractOperationType.getContractInstance,
        message: ContractOperationErrorMessage.getContractInstanceFailed,
      })
    );
  }
}

export function* watchEcaSaga() {
  yield all([takeLatest(getContractInstance.type, ecaSaga)]);
}
